import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Visualization } from './';
import { ChartMetadata, VisualizationType } from 'models/ai-chat/visualization.types';
import { FilterItem } from 'models/ai-chat/filter-request';
import AppliedFiltersDisplay from './AppliedFiltersDisplay';
import { downloadChartAsCSV } from 'utils/chartDataToCSV';

// Add global styles to ensure modal breaks out
if (typeof document !== 'undefined') {
  const styleId = 'chart-full-view-modal-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      #chart-full-view-portal-root {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 99999 !important;
        pointer-events: none !important;
        overflow: visible !important;
      }
      .chart-full-view-modal {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        z-index: 99999 !important;
        margin: 0 !important;
        padding: 0 !important;
        overflow: visible !important;
        max-height: none !important;
        max-width: none !important;
        background-color: white !important;
        display: flex !important;
        flex-direction: column !important;
        pointer-events: auto !important;
        isolation: isolate !important;
      }
      .chart-full-view-modal * {
        box-sizing: border-box;
      }
    `;
    document.head.appendChild(style);
  }
}

interface ChartFullViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any;
  type: VisualizationType | string;
  sqlQuery?: string;
  messageId?: string;
  metadata: ChartMetadata | null;
  onFilterValueChange?: (_filter: any, _newValue: string) => void;
  onFilterUpdateComplete?: (_updatedData: any) => void;
  filterData?: FilterItem[];
  customerId?: string;
  onExportExcel?: () => void;
  onDownloadPDF?: () => void;
}

const ChartFullViewModal: React.FC<ChartFullViewModalProps> = ({
  isOpen,
  onClose,
  data,
  type,
  sqlQuery,
  messageId,
  metadata,
  onFilterValueChange,
  onFilterUpdateComplete,
  filterData,
  customerId,
  onExportExcel,
  onDownloadPDF
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartData, setChartData] = useState(data);
  const [availableHeight, setAvailableHeight] = useState(600);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalContainer(document.body);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      // Lock body scroll when modal is open
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (data) {
      setChartData(data);
    }
  }, [data]);

  useEffect(() => {
    const calculateHeight = () => {
      if (!isOpen) return;

      setTimeout(() => {
        const viewportHeight = window.innerHeight;
        const filtersSection = document.querySelector('.filters-section');
        const chartContainer = document.querySelector('[data-chart-container]');
        const modalContent = document.querySelector('.modal-content');

        const filtersHeight = filtersSection ? filtersSection.getBoundingClientRect().height + 24 : 0;
        const containerPadding = chartContainer ?
          parseInt(getComputedStyle(chartContainer).paddingTop) + parseInt(getComputedStyle(chartContainer).paddingBottom) : 48;
        const contentPadding = modalContent ?
          parseInt(getComputedStyle(modalContent).paddingTop) + parseInt(getComputedStyle(modalContent).paddingBottom) : 48;

        const calculatedHeight = viewportHeight - filtersHeight - containerPadding - contentPadding;
        const finalHeight = Math.max(calculatedHeight, 400);
        setAvailableHeight(finalHeight);
      }, 100);
    };

    calculateHeight();

    const observer = new MutationObserver(() => {
      calculateHeight();
    });

    const filtersElement = document.querySelector('.filters-section');
    if (filtersElement) {
      observer.observe(filtersElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }

    window.addEventListener('resize', calculateHeight);

    return () => {
      window.removeEventListener('resize', calculateHeight);
      observer.disconnect();
    };
  }, [isOpen, sqlQuery]);

  const handleChartExport = () => {
    if (chartData) {
      // Ensure chartData has the correct structure for CSV export
      const exportData = {
        categories: chartData.categories || [],
        series: chartData.series || [],
        title: chartData.title || 'Chart Data',
        xAxisTitle: chartData.xAxisTitle || 'Category',
        yAxisTitle: chartData.yAxisTitle || 'Value'
      };

      downloadChartAsCSV(exportData);
    } else if (onExportExcel) {
      onExportExcel();
    }
  };

  if (!isOpen || !portalContainer) return null;

  const modalContent = (
    <div
      className="chart-full-view-modal"
      onClick={(e) => {
        e.stopPropagation();
      }}
      style={{
        backgroundColor: 'white',
        pointerEvents: 'auto'
      }}
    >
      {/* Collapse button is handled by MenuAction in AppliedFiltersDisplay below */}

      {/* Modal Content */}
      <div className="modal-content flex-1 overflow-y-auto px-6 pb-6 pt-6" style={{ overflow: 'auto' }}>
        <div className="w-full h-full flex flex-col">
          {/* Applied Filters */}
          {sqlQuery && (
            <div className="filters-section mb-6 flex-shrink-0">
              <AppliedFiltersDisplay
                sqlQuery={sqlQuery}
                onFilterValueChange={onFilterValueChange}
                onFilterUpdateComplete={onFilterUpdateComplete}
                customerId={customerId}
                filterData={filterData}
                messageId={messageId}
                hasChartSwitcher={false}
                pdfGeneration={metadata?.pdf_generation || false}
                onExportExcel={onExportExcel}
                onDownloadPDF={onDownloadPDF}
                chartData={chartData}
                chartType={type}
                chartMetadata={metadata}
                onFullView={() => {
                  onClose();
                  const event = new CustomEvent('chartModalClose');
                  document.dispatchEvent(event);
                }}
                isInModal={true}
              />
            </div>
          )}

          {/* Chart Container */}
          <div
            ref={chartContainerRef}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex-1 flex flex-col min-h-0"
            data-chart-container
          >
            <div className="w-full h-full flex-1 min-h-0">
              <Visualization
                data={chartData}
                type={type}
                sqlquery={sqlQuery}
                messageId={messageId}
                metadata={metadata}
                onFilterValueChange={onFilterValueChange}
                onFilterUpdateComplete={onFilterUpdateComplete}
                filterData={filterData}
                customerId={customerId}
                onExportExcel={onExportExcel}
                onDownloadPDF={onDownloadPDF}
                containerHeight={availableHeight}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, portalContainer);
};

export default ChartFullViewModal;
